/**
 * The text of a Word document (.docx), as lines in reading order (SEO_BRIEF_06 §14).
 *
 * A .docx is a ZIP of XML parts, and the body is one of them: `word/document.xml`. The caller
 * unzips that part with the site's own ZIP reader (src/worker/adapter/zip.ts) and hands its text
 * here; `isWordPackage` is how a file is recognised as Word from its entry names, never from the
 * extension it was saved under.
 *
 * WHAT IS READ, in document order:
 *
 *   • every paragraph (`w:p`) as one line, its runs' text (`w:t`) joined; a tab (`w:tab`,
 *     `w:ptab`) as a tab; a line break (`w:br`, `w:cr`) as the end of a line, so a line break
 *     inside a paragraph starts a new line just as a new paragraph does;
 *   • every table row (`w:tr`) as one line, built so the TXT reader sees what the row means: a
 *     row that starts with a time (`00:00:01,000 | 00:00:04,000 | Hello`, which is our own Word
 *     export) becomes `00:00:01,000 --> 00:00:04,000 Hello`, a leading subtitle number is
 *     dropped, and a short first cell before the text (`Anna | Hello`) is a speaker, `Anna: Hello`;
 *   • tracked insertions, hyperlinks, content controls, field RESULTS, and text boxes (once).
 *
 * WHAT IS NOT: headers, footers, footnotes and comments, which live in other parts of the ZIP and
 * are never opened; deleted and moved-away text (`w:del`, `w:moveFrom`); field codes
 * (`w:instrText`); and the fallback copy of a text box (`mc:Fallback`), which Word writes beside
 * the real one for older readers and which would otherwise read every text box twice.
 *
 * Formatting (bold, italic, fonts, colours) is not kept: the TXT reader takes text.
 *
 * PURE: the XML is read with the site's own reader (`common/xml.ts`), which never throws.
 */

import { parseXml, type XmlElement, type XmlNode } from '../common/xml.ts';

/** The part a WordprocessingML package keeps its body in. */
export const WORD_DOCUMENT_PART = 'word/document.xml';

/**
 * True when a ZIP's entry names make it a Word document: it has `word/document.xml`. Excel and
 * PowerPoint packages have `xl/` and `ppt/` instead, and a ZIP of subtitle files has neither.
 * Names are compared without case and with either slash, as some ZIP tools write them.
 */
export function isWordPackage(entryNames: readonly string[]): boolean {
  return entryNames.some((n) => n.replace(/\\/g, '/').toLowerCase() === WORD_DOCUMENT_PART);
}

/** WordprocessingML's main namespace: Transitional (what Word writes) and Strict. */
const W_NAMESPACES: ReadonlySet<string> = new Set([
  'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
  'http://purl.oclc.org/ooxml/wordprocessingml/main',
]);
const MC_NAMESPACE = 'http://schemas.openxmlformats.org/markup-compatibility/2006';

/** A WordprocessingML element by local name, or a `w:`-prefixed one whose namespace is unbound. */
function isW(el: XmlElement, local: string): boolean {
  if (el.local !== local) return false;
  return el.namespace === null ? el.prefix === 'w' : W_NAMESPACES.has(el.namespace);
}

function isMc(el: XmlElement, local: string): boolean {
  if (el.local !== local) return false;
  return el.namespace === null ? el.prefix === 'mc' : el.namespace === MC_NAMESPACE;
}

/** Subtrees whose text is not the document's text. */
const SKIPPED = new Set([
  'del',
  'moveFrom',
  'instrText',
  'delText',
  'delInstrText',
  'pPr',
  'rPr',
  'sectPr',
  'tblPr',
  'trPr',
  'tcPr',
  'tblGrid',
  'commentRangeStart',
  'commentRangeEnd',
]);

export interface DocxText {
  /** The document's lines in reading order. Empty paragraphs are empty strings. */
  readonly lines: readonly string[];
  /** The XML reader's complaints, for a diagnostic. A damaged part still gives what it could. */
  readonly problems: number;
}

/** Reads `word/document.xml`'s text. Never throws; a part with no body gives no lines. */
export function docxText(documentXml: string): DocxText {
  const doc = parseXml(documentXml);
  const lines: string[] = [];
  const body = doc.root === null ? null : findBody(doc.root);
  if (body !== null) blocks(body.children, lines);
  return { lines, problems: doc.problems.length };
}

function findBody(root: XmlElement): XmlElement | null {
  for (const child of root.children) {
    if (child.kind === 'element' && isW(child, 'body')) return child;
  }
  // Not a `w:document` we recognise: read whatever paragraphs it has, from the root.
  return root;
}

/** Block-level content: paragraphs and tables, and the containers that hold them. */
function blocks(nodes: readonly XmlNode[], out: string[]): void {
  for (const node of nodes) {
    if (node.kind !== 'element') continue;
    if (isW(node, 'p')) out.push(...paragraph(node));
    else if (isW(node, 'tbl')) table(node, out);
    else if (skipped(node)) continue;
    else if (isMc(node, 'AlternateContent')) blocks(choice(node), out);
    else blocks(node.children, out);
  }
}

function skipped(el: XmlElement): boolean {
  if (isMc(el, 'Fallback')) return true;
  return SKIPPED.has(el.local) && (el.namespace === null || W_NAMESPACES.has(el.namespace));
}

/** `mc:AlternateContent`: the first `mc:Choice`, or the fallback when there is no choice. */
function choice(el: XmlElement): readonly XmlNode[] {
  const elements = el.children.filter((c): c is XmlElement => c.kind === 'element');
  const first =
    elements.find((c) => isMc(c, 'Choice')) ?? elements.find((c) => isMc(c, 'Fallback'));
  return first === undefined ? [] : first.children;
}

/**
 * One paragraph as its lines: its own text, split at line breaks, then the paragraphs of any text
 * box anchored in it, after it.
 */
function paragraph(p: XmlElement): string[] {
  let text = '';
  const nested: string[] = [];
  const walk = (nodes: readonly XmlNode[]): void => {
    for (const node of nodes) {
      if (node.kind !== 'element') continue;
      if (skipped(node)) continue;
      if (isW(node, 't')) text += textOf(node);
      else if (isW(node, 'tab') || isW(node, 'ptab')) text += '\t';
      else if (isW(node, 'br') || isW(node, 'cr')) text += '\n';
      else if (isW(node, 'noBreakHyphen')) text += '-';
      else if (isW(node, 'txbxContent')) blocks(node.children, nested);
      else if (isMc(node, 'AlternateContent')) walk(choice(node));
      else walk(node.children);
    }
  };
  walk(p.children);
  return [...text.split('\n'), ...nested];
}

function textOf(el: XmlElement): string {
  let out = '';
  for (const child of el.children) if (child.kind === 'text') out += child.text;
  return out;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Tables
───────────────────────────────────────────────────────────────────────────────────────*/

/** A time and nothing else in a cell: `00:00:01,000`, `0:12`, `[01:23]`. */
const CLOCK_CELL = /^[[(]?\s*\d{1,3}:\d{1,2}(?::\d{1,2})?(?:[.,]\d{1,3})?\s*[\])]?$/;
/** A whole time range in one cell: `00:00:01,000 --> 00:00:04,000`. */
const RANGE_CELL = /^[[(]?\s*\d{1,3}:\d{1,2}(?::\d{1,2})?(?:[.,]\d{1,3})?\s*[\])]?\s*-+>?\s*\S/;
const NUMBER_CELL = /^\d{1,6}$/;
/** A row line that starts with a time, as `row` builds them. */
const CLOCK_HEAD = /^[[(]?\s*\d{1,3}:\d{1,2}/;
/** A speaker's name in a cell of its own: short, and not a sentence. */
const SPEAKER_CELL = /^[^\n.!?]{1,32}$/u;

/**
 * A table's rows, in order. When some rows start with a time and the first does not, the first is
 * the column headings (`Start | End | Text` in our own export) and is left out: it is not a line
 * anybody said.
 */
function table(tbl: XmlElement, out: string[]): void {
  const rows: string[][] = [];
  const walk = (nodes: readonly XmlNode[]): void => {
    for (const node of nodes) {
      if (node.kind !== 'element') continue;
      if (isW(node, 'tr')) rows.push(row(node));
      else if (!skipped(node)) walk(node.children); // w:sdt, w:customXml around rows
    }
  };
  walk(tbl.children);
  const timed = (lines: readonly string[]) => CLOCK_HEAD.test(lines[0] ?? '');
  if (rows.length > 1 && !timed(rows[0] as string[]) && rows.slice(1).some(timed)) rows.shift();
  for (const lines of rows) out.push(...lines);
}

/** One row's cells, each cell's paragraphs joined by a line break. */
function cellsOf(tr: XmlElement): string[] {
  const cells: string[] = [];
  const walk = (nodes: readonly XmlNode[]): void => {
    for (const node of nodes) {
      if (node.kind !== 'element' || skipped(node)) continue;
      if (isW(node, 'tc')) {
        const lines: string[] = [];
        blocks(node.children, lines);
        cells.push(
          lines
            .map((l) => l.trim())
            .filter((l) => l !== '')
            .join('\n'),
        );
      } else walk(node.children);
    }
  };
  walk(tr.children);
  return cells;
}

/**
 * A table row as the lines the TXT reader should see. A row whose cells hold several lines keeps
 * them: the first goes on the time's line and the rest follow it, which is how the TXT reader
 * reads a subtitle of two lines.
 */
function row(tr: XmlElement): string[] {
  let cells = cellsOf(tr).filter((c) => c !== '');
  if (cells.length === 0) return [''];
  const timed = (c: string | undefined) =>
    c !== undefined && (CLOCK_CELL.test(c) || RANGE_CELL.test(c.split('\n')[0] as string));
  if (cells.length > 1 && NUMBER_CELL.test(cells[0] as string) && timed(cells[1])) {
    cells = cells.slice(1);
  }
  let head = '';
  if (CLOCK_CELL.test(cells[0] as string)) {
    head = (cells[0] as string).trim();
    cells = cells.slice(1);
    if (cells[0] !== undefined && CLOCK_CELL.test(cells[0])) {
      head = `${head} --> ${cells[0].trim()}`;
      cells = cells.slice(1);
    }
  }
  if (cells.length === 2 && SPEAKER_CELL.test(cells[0] as string)) {
    cells = [`${(cells[0] as string).trim()}: ${cells[1] as string}`];
  }
  const text = cells.join(' ');
  const joined = head === '' ? text : text === '' ? head : `${head} ${text}`;
  return joined.split('\n');
}
